/* Third party libraries */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useDispatch } from 'react-redux';
import { notifyUser } from '../../../store/Toast';

import {
  deleteClientWidget,
  getClientWidgetsList,
  getDashboardData,
  getWidgetsList,
  postClientWidget,
  PostClientWidgetInterface,
  postWidgetsList,
  PostWidgetsListInterface,
} from '../../../api/analytics';
import { DashboardType } from '../../../types/Analytics';
import { useEffect, useState } from 'react';
import { WidgetListInterface } from '../../../components/Widgets/types';

export const useDashboardData = ({
  onSuccess,
  onError,
  dashboard,
  widget,
}: {
  onSuccess?: (data: any) => void;
  onError?: (data: any) => void;
  dashboard: DashboardType;
  widget: string;
}) => {
  return useQuery(
    [`get-${dashboard}-widgets-data`],
    () => getDashboardData(dashboard),
    {
      select: (resData: any) =>
        resData?.data?.find((d: any) => d?.slug === widget),
      onSuccess,
      onError,
      keepPreviousData: true,
      staleTime: 30000,
    }
  );
};

export const useDashboardWidgetsList = ({
  dashboard,
  onSuccess,
  onError,
}: {
  dashboard: DashboardType;
  onSuccess?: (data: any) => void;
  onError?: (data: any) => void;
}) => {
  const [isWidgetUsed, setIsWidgetUsed] = useState<{ [key: string]: boolean }>(
    {}
  );

  const fetchDashboardWidgets = useQuery(
    [`get-${dashboard}-widgets`],
    () => getWidgetsList(),
    {
      select: (resData: any) => {
        return resData?.data?.filter((res: any) => res?.slug === dashboard);
      },
      onSuccess,
      onError,
      keepPreviousData: true,
      staleTime: 30000,
    }
  );
  //Widget data transformation to use isUsed key for dashboard widget rendering.
  //didn't use onSuccess and select props of useQuery because those can be useful
  //on other purposes
  const widgetList = fetchDashboardWidgets?.data?.[0]?.widgets;
  useEffect(() => {
    const transformedWidgetList = widgetList?.reduce(
      (acc: {}, data: WidgetListInterface) => ({
        ...acc,
        [data.slug]: data.isUsed,
      }),
      {}
    );
    setIsWidgetUsed(transformedWidgetList);
  }, [widgetList]);
  return { isWidgetUsed, fetchDashboardWidgets };
};

export const useDashboardWidgetListUpdate = ({
  dashboard,
}: {
  dashboard: DashboardType;
}) => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  return useMutation(
    (data: PostWidgetsListInterface) => postWidgetsList(data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries([`get-${dashboard}-widgets`]);
        queryClient.invalidateQueries([`get-${dashboard}-widgets-data`]);
        dispatch(
          notifyUser({
            showToast: true,
            toastMessage: `Widget update successful`,
            toastType: 'success',
          })
        );
      },
      onError: () => {
        dispatch(
          notifyUser({
            showToast: true,
            toastMessage: `Widget update failed`,
            toastType: 'failed',
          })
        );
      },
    }
  );
};

export const useClientAccountWidgetList = ({
  accountId,
  widgetGroup,
  onSuccess,
  onError,
}: {
  accountId?: number;
  widgetGroup?: string;
  onSuccess?: (data: any) => void;
  onError?: (data: any) => void;
}) => {
  return useQuery(
    ['get-client-account-widgets', accountId],
    () => getClientWidgetsList(accountId),
    {
      select: (resData: any) => {
        if (widgetGroup) {
          return resData?.data?.find((wdg: any) => wdg.slug === widgetGroup);
        }
        return resData;
      },
      enabled: !!accountId,
      onSuccess,
      onError,
    }
  );
};

export const useAssignWidgetToClientAccount = () => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  return useMutation(
    (data: PostClientWidgetInterface) => postClientWidget(data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries([`get-client-account-widgets`]);
        dispatch(
          notifyUser({
            showToast: true,
            toastMessage: `Widget assigned successfully`,
            toastType: 'success',
          })
        );
      },
      onError: () => {
        dispatch(
          notifyUser({
            showToast: true,
            toastMessage: `Widget assign failed`,
            toastType: 'failed',
          })
        );
      },
    }
  );
};

export const useUnassignWidgetToClientAccount = () => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  return useMutation(
    (data: PostClientWidgetInterface) => deleteClientWidget(data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries([`get-client-account-widgets`]);
        dispatch(
          notifyUser({
            showToast: true,
            toastMessage: `Widget unassigned successfully`,
            toastType: 'success',
          })
        );
      },
      onError: () => {
        dispatch(
          notifyUser({
            showToast: true,
            toastMessage: `Widget already assigned to account.`,
            toastType: 'failed',
          })
        );
      },
    }
  );
};
